import { NOTIFICATION_CONTENT_LIST_MAX_LIMIT, type NotificationContent } from 'primary-schema';

import { type Common } from '../common.js';
import {
  type CloneResponse,
  type CloneValues,
  type CreateValues,
  type FindAllOptions,
  type SendTestResponse,
  type SendTestValues,
  type UpdateValues,
} from '../models/notification/content.js';
import { type PaginatedQueryOptions } from '../models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from '../models/pagination/PaginationResponse.type.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/notification-contents';
  }

  /** The largest page the endpoint serves, for a caller that groups or searches the set in the browser. */
  async findAll(options?: FindAllOptions): Promise<NotificationContent[]> {
    const url = this.common.constructPath(this.baseEndpoint, {
      ...options,
      limit: NOTIFICATION_CONTENT_LIST_MAX_LIMIT,
    });
    const page: PaginationResponse<NotificationContent> = await this.common.getJSON(url);
    return page.data;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<NotificationContent>> {
    // The endpoint has nothing sortable, so `sort` is dropped rather than sent to be rejected.
    const { page, sort, ...filters } = options;
    const url = this.common.constructPath(this.baseEndpoint, {
      ...filters,
      limit: page.size,
      cursor: 'after' in page ? page.after : undefined,
    });
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<NotificationContent> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<NotificationContent> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async clone(id: string, values: CloneValues): Promise<CloneResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/clone`);
    return await this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<NotificationContent> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async remove(id: string): Promise<{ success: true }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async sendTest(id: string, values: SendTestValues): Promise<SendTestResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/test`);
    return await this.common.postJSON(url, values);
  }
}
